/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductSpecificationData } from '@msdyn365-commerce-modules/product-specification/src/modules/product-specification/product-specification.data';
import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from '@msdyn365-commerce-modules/utilities';
import { PriceComponent, RatingComponent } from '@msdyn365-commerce/components';
import MsDyn365, { IComponentProps, IGridSettings, IImageSettings, Image, msdyn365Commerce, RichTextComponent } from '@msdyn365-commerce/core';
import { ChannelDeliveryOptionConfiguration, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { CartLine, DiscountLine, SalesLine } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import * as React from 'react';
/* VSI customization START */
import { IMFIBonusCouponsDetail, IMFIBonusItemDetails, IMFIBonusItemsResponse, IMFIGetBonusCouponsDetailsResponse } from '../../../../actions/BonusItemDataServiceEntities.g';
import {IProductWithAttribute} from '../../../../data-actions/get-product-attributes-cartlines';
import { ICoreDeliveryByMessage, IProductBonusItems, IProductBonusWithBrandName } from '../../../../modules/mfrm-cart/mfrm-cart';
import {hasArray} from '../../../../Utilities/emtpyChecker';
import {parseBrandName} from '../../../../Utilities/get-product-brand';
import { format } from '@msdyn365-commerce-modules/utilities';
import { getProductAlertBanner, isOneOfChildBonusCoupons } from '../../../../modules/mfrm-cart/utils/cart-utils';
import { urlFallBackImage } from '../../../../Utilities/fallbackImageUrl';
import IncrementalQuantity from '../../../../modules/mfrm-cart/components/IncrementalQuantity';
import { cartDeliveryMsgRegex } from '../../../../Utilities/allRegex';
import { ICartIconData, IEyeBrowIcon } from '../../../../Utilities/cart-utils';
import { EyeBrowAttributeName, EyeBrowMessage } from '../../../../Utilities/enum';
import { getBVAttributeValues } from '../../../../Utilities/get-bv-attributes';
import { cartModuleName } from '../../../../Utilities/enum';
import { _getProductBadges } from '../../../../Utilities/product-badge-utils';
import CustomSkeleton from '../../custom-components/CustomSkeleton';
/* VSI customization END */

/**
 * ICartlineResourceString: Interface for specifying the
 * resource strings that the component needs
 */
export interface ICartlineResourceString {
    /** Display string for discount label */
    discountStringText: string;
    /** String for size key  */
    sizeString: string;
    /** String for color key  */
    colorString: string;
    /** String associated with the configuration product dimension  */
    configString: string;
    /** String for style key  */
    styleString: string;
    /** String for amount key  */
    amountString?: string;
    /** Display string for quantity label  */
    quantityDisplayString: string;
    /** Display string for quantity label  */
    inputQuantityAriaLabel: string;
    /** display string for product link  */
    productPageLinkAriaLabel: string;
    /** Aria label for the decrement button in quanitity component  */
    decrementButtonAriaLabel?: string;
    /** Aria label for the increment button in quanitity component  */
    incrementButtonAriaLabel?: string;
    /** original text screenreader */
    originalPriceText: string;
    /** current text screenreader  */
    currentPriceText: string;
    /** shipping Charges Text  */
    shippingChargesText: string;
    /** Outofstock text Text  */
    outOfStockMsgText: string;
    /** remove from cart Text  */
    removeFromCartText: string;
    /** similar available products Text  */
    similarAvailableProductText: string;
    /** Aria Label Text for product  */
    productAriaText: string;
    variantAriaText: string;
    promoAriaText: string;
    priceAriaText: string;
    discountPercentageAriaText: string;
    editGiftCtaText: string;
    dialogAriaText: string;
    removeButtonAriaText: string;
    appliedPromoText: string;
    notAppliedPromoText: string;
    sixPlusWeeks: string;
    deliveryIn: string;
    freePriceText: string;
}

interface ICartLineData {
    cartLine: CartLine | SalesLine;
    product?: SimpleProduct;
    productSpecification?: IProductSpecificationData;
    productAttributes: IProductWithAttribute[] | undefined;
}

export type CartLineDisplayMode = 'DEFAULT' | 'COMPACT';

/**
 * ICartLineProps: The props required to render cartLineitem
 */
export interface ICartLineProps extends IComponentProps<ICartLineData> {
    /** The flag to change the quantity component from interactible to static  */
    disableQuantityInteractivity?: boolean;
    /** The primary image url  */
    primaryImageUrl?: string;
    /** The product url  */
    productUrl?: string;
    /** GridSettings for the product image in cartLine  */
    gridSettings: IGridSettings;
    /** ImageSettings for the product image in cartLine  */
    imageSettings: IImageSettings;
    /** Boolean flag to indicate if the item is out of stock  */
    isOutOfStock?: boolean;
    /** Flag to make quantity section editable  */
    isQuantityEditable?: boolean;
    /** Max quantity for line item  */
    maxQuantity?: number;
    /** Current quantity for line item  */
    currentQuantity?: number;
    /** Resource string for the component  */
    resources: ICartlineResourceString;
    /** SalesLine flag  */
    isSalesLine?: boolean;
    /** Error message to show in place of quantity */
    errorMessage?: string;
    /** Display mode to use */
    displayMode?: CartLineDisplayMode;
    /** Inventory information label  */
    inventoryInformationLabel?: string;
    /** Inventory information class name  */
    inventoryLabelClassName?: string;
    /** Flag to show/hide shipping charges for line items  */
    showShippingChargesForLineItems?: boolean;
    /** Boolean flag to indicate if cart state status is ready  */
    isCartStateReady?: boolean;
    /**  Chanel Delivery Option configuration is from api. */
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
    /** The telemetry content */
    telemetryContent?: ITelemetryContent;
    lineIndex?: number;
    /* VSI customization START */
    bonusItemsProductDetails?: IMFIBonusItemsResponse[];
    GiftCtaButton?: boolean;
    mfrmCtaGiftText?: string;
    mfrmBonusModalHeading?: string;
    mfrmBonusSubHeading?: string;
    mfrmBonusAppliedCodeText?: string;
    mfrmAddItemToCartText?: string;
    sendStateValue?: IProductBonusItems[];
    productId?: number;
    deliveryByMessagesCore: ICoreDeliveryByMessage[];
    deliveryByMessageSPDS: ICoreDeliveryByMessage[];
    hideDeliveryMessage:boolean;
    // tslint:disable-next-line
    ctaGiftHandler?(cartLine: CartLine, actionContext: any): void;
    recievedBonusProductFromCartLine?(productId: number | undefined, bonusModalProduct: IMFIBonusItemDetails[], productQuantityCount: number): void;
    setCartLine?(obj: IProductBonusItems): void;
    /* VSI customization END */
    /** Quantity onChange callback  */
    quantityOnChange?(cartLine: CartLine, newQuantity: number): Promise<boolean>;
    removeCartLine?(cartLine: CartLine, url: string): void;
    onEditGift?(): void;
    productDeleteIcon?: ICartIconData;
    productDecrementIcon?: ICartIconData;
    productIncrementIcon?: ICartIconData;
    isMobile?: boolean;
    eyeBrowIcons?: IEyeBrowIcon[];
    emptydeliveryMsgForCore?: boolean; // Check if mFIATPInventoryPLP API gives empty message, don't show skeleton for delivery message
}

interface IDimensionStrings {
    /** String for size key  */
    sizeString: string;
    /** String for color key  */
    colorString: string;
    /** String associated with the configuration product dimension  */
    configString: string;
    /** String for style key  */
    styleString: string;
    /** String for amount key  */
    amountString?: string;
}

const isQualifying = (cl: CartLine, coupon: string): boolean => {
    const getLocalStorage = localStorage.getItem('BonusItemProduct');
    let bonusItemArray: IMFIBonusItemsResponse[] = [];
    if (getLocalStorage) {
        const localStorageBonusInfo = JSON.parse(getLocalStorage);
            // get bonus item array from localstorage
        if(!hasArray(localStorageBonusInfo)) {
            if(localStorageBonusInfo && hasArray(localStorageBonusInfo.BonusItemsDetails)) {
                bonusItemArray = [localStorageBonusInfo];
            }
        } else {
            bonusItemArray = localStorageBonusInfo;
        }
        if(!hasArray(bonusItemArray)) {
            return false;
        }
        // get bonus item array from localstorage
        for(const bonusItemsArray of bonusItemArray) {
        // bonusItemArray.map((bonusItemsArray) => {
            // bonus product array against applied promocode
            if(bonusItemArray && bonusItemsArray.BonusItemsDetails) {
                for(const bonusItemDetail of bonusItemsArray.BonusItemsDetails) {
                // bonusItemsArray.BonusItemsDetails?.map((bonusItemDetail) => {
                    // bonus item details array with items of parent product
                    if(bonusItemDetail && bonusItemDetail.Items && (bonusItemDetail.Coupon?.toUpperCase() === coupon.toUpperCase())) {
                        for(const item of bonusItemDetail.Items) {
                            // bonusItemDetail.Items?.map((item) => {
                                // check if cartlineId is equal to parent or qualifying Id
                                    if (item.QualifyingItemId === cl.ItemId && item.QualifyingProductId?.toString() === cl.ProductId?.toString()) {
                                        return true;
                                    }
                            // });
                        }
                    }
                }
            }
        }
    }
    return false;
};
export const _isABonusCoupon = (appliedPromoCoupons: string): boolean => {
    let checkBonusCoupons: boolean | undefined = false;
    const getLocalstorageBonusCoupons = localStorage.getItem('bonusCouponsDetail');
        if (getLocalstorageBonusCoupons) {
            const bonusCouponsArray: IMFIGetBonusCouponsDetailsResponse[] = JSON.parse(getLocalstorageBonusCoupons);
            bonusCouponsArray?.map(coupons => {
                checkBonusCoupons = coupons.BonusCoupons?.some(item => item.BonusCoupon?.toUpperCase() === appliedPromoCoupons.toUpperCase() || (item.BonusCoupon && isOneOfChildBonusCoupons(appliedPromoCoupons, item.BonusCoupon)));
                return checkBonusCoupons;
            });
        }
    return checkBonusCoupons;
};

const getUniqueCouponsString = (props: ICartLineProps, isGiftCoupon?: IMFIBonusCouponsDetail[], discountLines?: DiscountLine[]) => {
    const couponsStringArray: string[] = [];
    // Pick each cartline's unique promocode and store them in localstorage when cartlines are being rendering so there'd be no need to check for applied promocodes again & again in promocode.component.tsx,
    const allPromoCodes = MsDyn365.isBrowser && localStorage.getItem('allPromoCodes'); // allPromoCodes has Values of All promocodes applied in the cart irrespective of type of coupon
    const uniquePromoCodes: string[] = allPromoCodes ? JSON.parse(allPromoCodes) : [];

    if(hasArray(isGiftCoupon) && hasArray(discountLines)) {
        discountLines?.map((discountLine, index) => {
            isGiftCoupon?.map((coupons, ind) => {
                    if(!(coupons.BonusCoupon && isQualifying(props.data.cartLine, coupons.BonusCoupon) && _isABonusCoupon(coupons.BonusCoupon))) {
                        let promoStr = '';
                        const bonusCouponStr = discountLine.DiscountCode && (discountLine.DiscountCode === coupons.BonusCoupon  || (coupons.BonusCoupon && _isABonusCoupon(coupons.BonusCoupon) && isOneOfChildBonusCoupons(discountLine.DiscountCode, coupons.BonusCoupon))) && (discountLine.EffectiveAmount && discountLine.EffectiveAmount > 0) ? (coupons.BonusCoupon ? coupons.BonusCoupon : '') : '';
                        // bonusCouponStr defines promocode of each cartline respectively
                        bonusCouponStr && uniquePromoCodes.indexOf(bonusCouponStr) === -1 && uniquePromoCodes.push(bonusCouponStr);
                        const str = `${bonusCouponStr} ${(discountLine.DiscountCode && (discountLine.DiscountCode === coupons.BonusCoupon || (coupons.BonusCoupon && _isABonusCoupon(coupons.BonusCoupon) && isOneOfChildBonusCoupons(discountLine.DiscountCode, coupons.BonusCoupon))) && (discountLine.EffectiveAmount && discountLine.EffectiveAmount > 0)? `${props.resources.appliedPromoText}` : '')}`;
                        promoStr = promoStr + str;
                        if(!couponsStringArray.includes(promoStr)) {
                            couponsStringArray.push(promoStr);
                        }
                    }
            });
        });
    }
    MsDyn365.isBrowser && props.data.cartLine && localStorage.setItem('allPromoCodes', JSON.stringify(uniquePromoCodes));
    return couponsStringArray;
};

const CartLineItemFunctions = {
    renderProductDimensions: (Product: SimpleProduct, DimensionStrings: IDimensionStrings) => {
        if (!Product || !Product.Dimensions) {
            return [];
        }
        return Product.Dimensions.map((productDimension, index) => {
            const addComma = (Product && Product.Dimensions && Product.Dimensions.length > 1 && (Product.Dimensions.length - 2) === index) ? '' : ' ,';
            if (productDimension.DimensionTypeValue === 1) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions1`} className='msc-cart-line__product-variant-item'>
                        {/** VSI Customization - Start - 14-12-2020 */}
                        {/** Commented span for label in cart variants */}
                        {/* <span className='msc-cart-line__product-variant-color'>{DimensionStrings.colorString} */}
                        <span className='name'>{productDimension.DimensionValue && productDimension.DimensionValue.Value}</span>
                        {/* </span> */}
                        {addComma}
                        {/** VSI Customzation - End - 14-12-2020 */}
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === 2) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions2`} className='msc-cart-line__product-variant-item'>
                        {/** VSI Customization - Start - 14-12-2020 */}
                        {/** Commented span for label in cart variants */}
                        {/* <span className='msc-cart-line__product-configuration'>{DimensionStrings.configString} */}
                        <span className='name'>{productDimension.DimensionValue && productDimension.DimensionValue.Value}</span>
                        {/* </span> */}
                        {addComma}
                        {/** VSI Customzation - End - 14-12-2020 */}
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === 3) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions3`} className='msc-cart-line__product-variant-item'>
                        {/** VSI Customization - Start - 14-12-2020 */}
                        {/** Commented span for label in cart variants */}
                        {/* <span className='msc-cart-line__product-variant-size'>{DimensionStrings.sizeString} */}
                        <span className='name'>{productDimension.DimensionValue && productDimension.DimensionValue.Value}</span>
                        {/* </span> */}
                        {addComma}
                        {/** VSI Customzation - End - 14-12-2020 */}
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === 4) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions4`} className='msc-cart-line__product-variant-item'>
                        {/** VSI Customization - Start - 14-12-2020 */}
                        {/** Commented span for label in cart variants */}
                        {/* <span className='msc-cart-line__product-variant-style'>{DimensionStrings.styleString} */}
                        <span className='name'>{productDimension.DimensionValue && productDimension.DimensionValue.Value}</span>
                        {/* </span> */}
                        {addComma}
                        {/** VSI Customzation - End - 14-12-2020 */}
                    </div>
                );
            }
            return null;
        });
    },
    getProductVariants: (Product: SimpleProduct, DimensionStrings: IDimensionStrings) => {
        return Product.Dimensions?.map((productDimension) => {
            switch (productDimension.DimensionTypeValue) {
                case 2:
                case 4:
                    return '';
                default:
                    return productDimension.DimensionValue && productDimension.DimensionValue.Value;
            }
        });
    },
    renderDiscountLines: (props: ICartLineProps) => {
        const isGiftCoupon: IMFIBonusCouponsDetail[] = [];
        let bonusCouponsArray: IMFIGetBonusCouponsDetailsResponse[] = [];
        let flagBonus: boolean = false;
        if(MsDyn365.isBrowser) {
            const getLocalstorageBonusCoupons = localStorage.getItem('bonusCouponsDetail');
            if (getLocalstorageBonusCoupons) {
                bonusCouponsArray = JSON.parse(getLocalstorageBonusCoupons);
                props.data.cartLine.DiscountLines && props.data.cartLine.DiscountLines.map((userDefinePromo) => {
                    flagBonus = false;
                    bonusCouponsArray.map((bonusCoupons) => {
                        bonusCoupons.BonusCoupons?.map((coupons) => {
                            if (coupons.BonusCoupon === userDefinePromo.DiscountCode) {
                                isGiftCoupon.push(coupons);
                            } else {
                                coupons.ChildCoupons?.map((childCoupons) => {
                                    if (childCoupons === userDefinePromo.DiscountCode) {
                                        flagBonus = true;
                                        if (isGiftCoupon.indexOf(coupons) === -1) {
                                            isGiftCoupon.push(coupons);
                                        }
                                    }
                                });
                            }

                            return [];
                        });
                    });
                    const defaultBonus: IMFIBonusCouponsDetail = {
                        BonusCoupon: userDefinePromo.DiscountCode
                    };
                    if (isGiftCoupon.indexOf(defaultBonus) === -1) {
                        !flagBonus && isGiftCoupon.push(defaultBonus);
                    }
                });
            }
        }
        if (!props.data.cartLine.DiscountLines || props.data.cartLine.DiscountLines.length === 0) {
            return null;
        }
        const uniqueCoupons = getUniqueCouponsString(props, isGiftCoupon, props.data.cartLine.DiscountLines);
        return (
            <>
                {uniqueCoupons.map((coupons, ind) => {
                    return (
                        <div key={props.data.cartLine.ProductId} title={coupons} className='msc-cart-line-item-product-discount'>
                            <span className='msc-cart-line__promo-codes' key={ind}>
                                {coupons}
                            </span>
                        </div>
                    );
                })
                }
            </>
        );
    },
    renderInventoryLabel: (props: ICartLineProps) => {
        if (!props.inventoryInformationLabel) {
            return null;
        }
        const inventoryCssName = props.inventoryLabelClassName ? `msc-cart-line__product-inventory-label ${props.inventoryLabelClassName}` : 'msc-cart-line__product-inventory-label';
        return (
            <span className={inventoryCssName}>{props.inventoryInformationLabel}</span>
        );
    },
    renderShippingLabel: (props: ICartLineProps) => {
        const pickupDeliveryModeCode = props.context.request.channel?.PickupDeliveryModeCode;
        const channelDeliveryOptionConfig = props.channelDeliveryOptionConfig;
        const cartline = props.data.cartLine;
        let hasShippingMethod = false;
        if (channelDeliveryOptionConfig !== undefined) {
            hasShippingMethod = !!cartline.DeliveryMode && channelDeliveryOptionConfig?.PickupDeliveryModeCodes?.some(deliveryMode => deliveryMode !== cartline.DeliveryMode) ? true : false;
        } else {
            hasShippingMethod = !!cartline.DeliveryMode && cartline.DeliveryMode !== pickupDeliveryModeCode ? true : false;
        }
        if (!hasShippingMethod) {
            return undefined;
        }

        const shippingChargeLines = (cartline.ChargeLines || []).filter(chargeLine => chargeLine.IsShipping);

        if (shippingChargeLines.length === 0) {
            return undefined;
        }

        const freightFee = shippingChargeLines.reduce((chargeTotal, chargeLine) => {
            return chargeTotal + (chargeLine.CalculatedAmount || 0);
        }, 0);

        const priceComponent = (
            <PriceComponent
                data={{
                    price: {
                        // @ts-ignore
                        CustomerContextualPrice: freightFee
                    }
                }}
                freePriceText={'FREE'}
                context={props.context}
                id={props.id}
                typeName={props.typeName}
                className='msc-cart-line__freight-actual'
            />
        );
        return (
            <>
                <label className='msc-cart-line__freight-label'>{`${props.resources.shippingChargesText}:`}</label>
                <span className='shipping-value'>
                    {priceComponent}
                </span>
            </>
        );
    },
    renderOtherCharges: (props: ICartLineProps) => {
        const cartline = props.data.cartLine;
        const otherCharges = cartline.ChargeLines?.filter((chargeline) => !chargeline.IsShipping);

        return otherCharges && otherCharges.map((otherCharge, key) => {
            return otherCharge && otherCharge.CalculatedAmount ? (
                <div className='msc-cart-line__other-charges' key={key}>
                    <label className='msc-cart-line__other-charges-label'>{`${otherCharge.Description}:`}</label>
                    <span className='other-charge-value'>
                        <PriceComponent
                            data={{
                                price: {
                                    // @ts-ignore
                                    CustomerContextualPrice: otherCharge.CalculatedAmount
                                }
                            }}
                            context={props.context}
                            id={props.id}
                            typeName={props.typeName}
                            className='msc-cart-line__other-charges-actual'
                        />
                    </span>
                </div>
            ) : '';
        }) || undefined;
    }
};


const renderEyeBrowIcon = (eyeBrowIcons: IEyeBrowIcon[] | undefined, attributeName: string ) => {
    let iconURL;
    if (eyeBrowIcons && eyeBrowIcons.length > 0) {
        iconURL = eyeBrowIcons?.find(item => item.iconName === EyeBrowAttributeName[attributeName])?.iconURL;
        if (iconURL) {
            return <img className='msc-cart-line__product-eye-brow-image' src={iconURL} alt={EyeBrowMessage[attributeName]} aria-hidden={true}/>;
        }
    }
    return null;
};
const getEyeBrowMsg = (productAttributes: IProductWithAttribute[] | undefined , productRecordId: number | undefined, eyeBrowIcons: IEyeBrowIcon[]  | undefined) => {
    let eyeBrowMsg = null;
    const getAttributes = productAttributes?.find((items) => items.productId === productRecordId)?.attributeValue;
    if(getAttributes) {
        for (const item of getAttributes) {
            if (item.Name === EyeBrowAttributeName.BestSeller && item.BooleanValue) {
                eyeBrowMsg = <>{renderEyeBrowIcon(eyeBrowIcons, EyeBrowAttributeName.BestSeller)} <p className='msc-cart-line__product-eye-brow-message'>{EyeBrowMessage.BestSeller}</p></>;
                break;
            } else if (item.Name === EyeBrowAttributeName.SE_Pick && item.BooleanValue) {
                eyeBrowMsg = <>{renderEyeBrowIcon(eyeBrowIcons, EyeBrowAttributeName.SE_Pick)} <p className='msc-cart-line__product-eye-brow-message'>{EyeBrowMessage.SleepExpertPick}</p></>;
                break;
            } else if (item.Name === EyeBrowAttributeName.IsExclusive && item.BooleanValue) {
                eyeBrowMsg = <>{renderEyeBrowIcon(eyeBrowIcons, EyeBrowAttributeName.IsExclusive)} <p className='msc-cart-line__product-eye-brow-message'>{EyeBrowMessage.OnlyAtMattressFirm}</p></>;
                break;
            } else if (item.Name === EyeBrowAttributeName.SleepScore && item.BooleanValue) {
                eyeBrowMsg = <>{renderEyeBrowIcon(eyeBrowIcons, EyeBrowAttributeName.SleepScore)} <p className='msc-cart-line__product-eye-brow-message'>{EyeBrowMessage.SleepScoreValidated}</p></>;
                break;
            } else if (item.Name === EyeBrowAttributeName.PLP_Ribbon && item.BooleanValue) {
                eyeBrowMsg = <>{renderEyeBrowIcon(eyeBrowIcons, EyeBrowAttributeName.PLP_Ribbon)} <p className='msc-cart-line__product-eye-brow-message'>{EyeBrowMessage.BestSeller}</p></>;
                break;
            } else {
                eyeBrowMsg = null;
            }
        }
    }
    return eyeBrowMsg;
};

// tslint:disable-next-line:max-func-body-length cyclomatic-complexity
const CartLine: React.FC<ICartLineProps> = (props: ICartLineProps) => {
    const { isSalesLine, productUrl, resources } = props;
    const { product, cartLine, productAttributes } = props.data;
    const [isShowProductModal, setIsShowProductModal] = React.useState(false);
    const promoRef = React.useRef<HTMLDivElement>(null);
    // const [selectedBonusItems, setselectedBonusItems] = React.useState<IMFIBonusItemDetails[]>(props?.sendStateValue);
    const destructDimensionStrings = {
        sizeString: resources.sizeString,
        colorString: resources.colorString,
        styleString: resources.styleString,
        configString: resources.configString,
        amountString: resources.amountString
    };
    const emptyPlaceHolderImage = props.context.app.config.placeholderImageName as string;
    const fallbackImageUrl = urlFallBackImage(emptyPlaceHolderImage, product?.ItemId, props.context.actionContext.requestContext);
    const imageSettings = props.imageSettings;
    imageSettings.cropFocalRegion = !ObjectExtensions.isNullOrUndefined(imageSettings);
    const { productPageLinkAriaLabel, productAriaText, variantAriaText, promoAriaText, priceAriaText, discountPercentageAriaText, discountStringText, editGiftCtaText, dialogAriaText } = props.resources;
    const productDimensions = product && CartLineItemFunctions.renderProductDimensions(product, destructDimensionStrings);
    const productDimensionVariants = product && CartLineItemFunctions.getProductVariants(product, destructDimensionStrings)?.toString();
    const renderDisountLines = CartLineItemFunctions.renderDiscountLines(props);
    const renderInventoryLabel = CartLineItemFunctions.renderInventoryLabel(props);
    const renderShippingLabel = CartLineItemFunctions.renderShippingLabel(props);
    const renderOtherCharges = CartLineItemFunctions.renderOtherCharges(props);
    const payLoad = getPayloadObject('click', props.telemetryContent!, '', product?.RecordId.toString());
    const prodAttribute = getTelemetryAttributes(props.telemetryContent!, payLoad);
    const attr = hasArray(productAttributes)?.find(p => p?.productId === product?.RecordId)?.attributeValue;
    const brandName = parseBrandName(attr, props.context.app.config.brandBackofficeAttributePdp, product?.RecordId);
    const productWithBrandName = product ? brandName ? `${brandName} ${product.Name}` : product.Name : cartLine.Description;
    if(MsDyn365.isBrowser) {
        const getALLCouponsDetailsWithoutParse = localStorage.getItem('bonusCouponsDetail');
        const getALLCouponsDetailsWithParse: IMFIGetBonusCouponsDetailsResponse[] = [];
        getALLCouponsDetailsWithParse.push(getALLCouponsDetailsWithoutParse && JSON.parse(getALLCouponsDetailsWithoutParse));
    }
    let bonusItemArray: IMFIBonusItemsResponse[] = [];
    let bonusItemProduct: boolean = false;
    let editGiftCta: boolean = false;
    let showModal: boolean = false;
    let parentProduct: boolean = false;
    const alredySelectedInModal: IMFIBonusItemDetails[] = [];

    const onChange = (newValue: number): Promise<boolean> => {
        if (props.quantityOnChange) {
            const result: Promise<boolean> = props.quantityOnChange(props.data.cartLine, newValue);
            return result;
        }
        return Promise.resolve(true);
    };

    const onDelete = () => {
        if (props.removeCartLine) {
            props.removeCartLine(props.data.cartLine, '');
        }
    };

    const _generateErrorMessage = (): JSX.Element | null => {
        if (props.errorMessage) {
            return (
                <div className='msc-alert__header'>
                    <span className='msi-exclamation-triangle' />
                    <span>{props.errorMessage}</span>
                </div>
            );
        }

        return null;
    };

    /* VSI Customization - Start - 14-12-2020 */
    /** Added this fucntion for formatting of prices  */
    const formatCurrency = (price: number | undefined): string | undefined => {
        if (price === undefined) {
            return undefined;
        }

        return props.context.cultureFormatter.formatCurrency(price);
    };
    /** VSI Customization - Start */

    if (props.bonusItemsProductDetails && props.bonusItemsProductDetails.length === 0) {
        if(MsDyn365.isBrowser) {
            const getLocalstorage = localStorage.getItem('BonusItemProduct');
            if (getLocalstorage) {
                bonusItemArray = JSON.parse(getLocalstorage);
            }
        }
    } else {
        if (props.bonusItemsProductDetails) {
            bonusItemArray = props.bonusItemsProductDetails;
        }
    }
    const BonusDetailsArray: IMFIBonusItemsResponse[] = bonusItemArray;
    hasArray(bonusItemArray)?.map((bonusitemdetail: IMFIBonusItemsResponse) => {
        bonusitemdetail.BonusItemsDetails?.map((bonusitem) => {

            // Map on child Bonus items if availible
            bonusitem.Items?.map((item) => {
                if (item.QualifyingItemId === cartLine.ItemId) {
                    parentProduct = true;
                    // addGiftCTA = true;
                    // setAddtoCart(true);
                }

                // Map on child Bonus items if availible
                item.BonusItems?.map((currentItem) => {
                    if (currentItem.BonusItemId === cartLine.ItemId && currentItem.BonusProductId?.toString() === cartLine.ProductId?.toString()) {
                        alredySelectedInModal.push(currentItem);
                        if (cartLine.DiscountLines && cartLine.DiscountLines.length > 0) {
                            for(const dl of props.data.cartLine.DiscountLines!) {
                                if(dl.DiscountCode && dl.DiscountCode.length && (_isABonusCoupon(dl.DiscountCode))) {
                                    bonusItemProduct = true;
                                }
                            }
                        }
                        if (currentItem.AdditionToCart === 'Manual') {
                            if (cartLine.DiscountLines && cartLine.DiscountLines.length > 0) {
                                for(const dl of props.data.cartLine.DiscountLines!) {
                                    if(dl.DiscountCode && dl.DiscountCode.length && _isABonusCoupon(dl.DiscountCode)) {
                                        editGiftCta = true;
                                    }
                                }
                            }
                        }
                    }
                });
            });
        });
    });

    const trueProductModal = () => {
        setIsShowProductModal(true);
    };
    const falseProductModal = () => {
        setIsShowProductModal(false);
    };

    // Add gift CTA handler for bonus product
    const _addGiftItems = () => {
        if (props.ctaGiftHandler) {
            props.ctaGiftHandler(props.data?.cartLine, props.context);
            const getLocalStorage = localStorage.getItem('BonusItemProduct');
            let bonusItemArrayForModal: IMFIBonusItemsResponse[] = [];
            // let showModal:boolean = false;
            if (getLocalStorage) {
                bonusItemArrayForModal = JSON.parse(getLocalStorage);
                bonusItemArrayForModal?.map((bonusitemdetail) => {
                    bonusitemdetail.BonusItemsDetails?.map((bonusitem) => {
                        bonusitem.Items?.map((item) => {
                            item.BonusItems?.map(async (productItem) => {
                                if (productItem.AdditionToCart === 'Manual') {
                                    showModal = true;
                                }
                            });
                        });
                    });
                });
                if (showModal) {
                    trueProductModal();
                }
            }
        }
        if(props.onEditGift) {
            props.onEditGift();
        }
    };

    const _renderImages = (BonusProduct: IMFIBonusItemDetails) => {
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: `w=95&h=76&m=6`, w: 0, h: 0 }
            },
            disableLazyLoad: true
        };
        if (BonusProduct) {
            const emptyPlaceHolderImage = props.context.app.config.placeholderImageName as string;
            const bonusFallbackImageUrl = urlFallBackImage(emptyPlaceHolderImage, BonusProduct?.BonusItemId, props.context.actionContext.requestContext);
            return (
                <Image
                    src={BonusProduct.PrimaryImageUrl || bonusFallbackImageUrl}
                    imageSettings={defaultImageSettings}
                    gridSettings={props.context.request.gridSettings!}
                    altText={BonusProduct.Name}
                    loadFailureBehavior='empty'
                    fallBackSrc={bonusFallbackImageUrl}
                />
            );
        }
        return null;
    };
    let totalBonusSelectedItems: number;
    let userSelectedItems: number | undefined;
    let productParentQuantityCount: number = 1;
    let couponsApplied: string | undefined;
    let selectedParentProduct: IProductBonusItems = { parentId: undefined, children: [] };

    // tslint:disable-next-line: max-func-body-length
    const _createModal = () => {
        let parentId: number | undefined = props?.productId;
        let parentState: IProductBonusItems[] | undefined = props?.sendStateValue;
        if (isShowProductModal) {
            // tslint:disable-next-line: max-line-length
            BonusDetailsArray.map((bonusDetails) => bonusDetails.BonusItemsDetails?.map((bonusItemDetails) => bonusItemDetails.Items?.map((items) => items.BonusItems?.sort((previousDiscountedPrice,nextDiscountedPrice)=> previousDiscountedPrice.BonusCalculatedDiscountPrice - nextDiscountedPrice.BonusCalculatedDiscountPrice).map((bonusitem) => {
                if (Number(bonusitem?.BonusProductId) === props?.productId) {
                    parentId = Number(items?.QualifyingProductId);
                }
            }))));
            if (parentId) {
                props?.sendStateValue && props?.sendStateValue.length > 0 && props?.sendStateValue?.map(obj => {
                    if (parentId === Number(obj?.parentId)) {
                        selectedParentProduct = obj;
                    }
                });
            } else {
                parentId = props?.productId;
                parentState = props?.sendStateValue;
            }
        }
        let showProductModalLocal = true;
        hasArray(BonusDetailsArray)?.map((bonusDetails: IMFIBonusItemsResponse) => bonusDetails.BonusItemsDetails?.map((bonusItemDetails) => {
            if(bonusItemDetails.Items && bonusItemDetails.Items.length && bonusItemDetails.Items.length > 0) {
                bonusItemDetails.Items.map((bonusItem) => {
                    if(parentId && bonusItem.QualifyingProductId === parentId.toString()) {
                        couponsApplied = bonusItemDetails.Coupon;
                        bonusItem.BonusItems && bonusItem.BonusItems.length && bonusItem.BonusItems.map((bonusProduct) => {
                            if(bonusProduct.AdditionToCart === 'Auto') {
                                showProductModalLocal = false;
                            }
                        });
                    }
                });
            }
        }));

        if(!showProductModalLocal) {
            return ('');
        }
        return (isShowProductModal && (
            <Modal
                autoFocus={true}
                fade={false}
                isOpen={isShowProductModal}
                horizontalPosition={'center'}
                verticalPosition={'center'}
                zIndex={5003}
                toggle={falseProductModal}
                applicationNode={'rsg-root'}
                className={'msc-gift-modal'}
            >
                <ModalHeader toggle={falseProductModal} />
                <ModalBody>
                    <h2>{props.mfrmBonusModalHeading}</h2>
                    <p>{props.mfrmBonusSubHeading} <span>{props.mfrmBonusAppliedCodeText} {couponsApplied}</span></p>
                    <div className='msc-gift__bonus_container'>
                        {BonusDetailsArray.map((bonusDetails) => bonusDetails.BonusItemsDetails?.map((bonusItemDetails) => bonusItemDetails.Items?.map((items) => {
                            !selectedParentProduct?.parentId && parentState && parentState.length > 0 && parentState?.map(obj => {
                                if (parentId === Number(obj?.parentId)) {
                                    selectedParentProduct = obj;
                                }
                            });
                            if (Number(items?.QualifyingProductId) === parentId) {
                                return items.BonusItems?.map((bonusitem) => {
                                    const selected = selectedParentProduct && selectedParentProduct.children.filter(itemsSelected => itemsSelected.BonusProductId === bonusitem.BonusProductId);
                                    totalBonusSelectedItems = items.BonusProductQty;
                                    userSelectedItems = selectedParentProduct?.children?.length;
                                    if (items.QualifyingItemId === cartLine.ItemId && cartLine.Quantity) {
                                        productParentQuantityCount = cartLine.Quantity;
                                    }
                                    let productBrandName = '';
                                    const getLocalStorage = localStorage.getItem('bonusItmesWithBrand');
                                    let bonusItemBrandArray: IProductBonusWithBrandName[] = [];
                                    if (getLocalStorage) {
                                        bonusItemBrandArray = JSON.parse(getLocalStorage);
                                    }
                                    const checkBrandName = bonusItemBrandArray.find(item => item.productId === Number(bonusitem.BonusProductId))?.brandName;
                                    if(checkBrandName) {
                                        productBrandName = checkBrandName;
                                    }
                                    // userSelectedItems === totalBonusSelectedItems ? addGiftCTA = false : addGiftCTA = true;
                                    // const slectedClass = selected && selected.length > 0 ? 'selected msc-gift__bonus-block' : (userSelectedItems === totalBonusSelectedItems && selected && selected.length === 0) ? 'disabled msc-gift__bonus-block' : 'msc-gift__bonus-block';
                                    const slectedClass = selected && selected.length > 0 ? 'selected msc-gift__bonus-block' : (userSelectedItems === totalBonusSelectedItems && selected && selected.length === 0) ? 'msc-gift__bonus-block' : 'msc-gift__bonus-block';

                                    return (
                                        // tslint:disable-next-line: jsx-no-lambda max-line-length
                                        // <button role='button' className={slectedClass} key={bonusitem.BonusProductId} onClick={() => { _getSelectedBonusItems(bonusitem, parentId, parentState); }} disabled={(userSelectedItems === totalBonusSelectedItems && selected?.length === 0)}>
                                        <button role='button' className={slectedClass} key={bonusitem.BonusProductId} onClick={() => { _getSelectedBonusItems(bonusitem, parentId, parentState); }}>
                                            <div className='msc-gift__bonus__image'>{_renderImages(bonusitem)}</div>
                                            <div className='msc-gift__bonus__content'>
                                                <div className='msc-gift__bonus__name'>{productBrandName} {bonusitem.Name}</div>
                                                <div className='msc-gift__bonus__price'>{bonusitem.BonusCalculatedDiscountPrice > 0 ? props.context.cultureFormatter.formatCurrency(bonusitem.BonusCalculatedDiscountPrice) : 'FREE'}</div>
                                                <span className='msc-price__strikethrough' aria-hidden='true'>
                                                    {bonusitem.BonusCalculatedDiscountPrice > 0 ?
                                                        props.context.cultureFormatter.formatCurrency(bonusitem.CustomerContextualPrice) : props.context.cultureFormatter.formatCurrency(bonusitem.CustomerContextualPrice)}
                                                </span>
                                            </div>
                                        </button>
                                    );
                                });
                            } else {
                                return;
                            }
                        })))}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className='msc-gift__bonus__count'>{userSelectedItems} of {totalBonusSelectedItems} items selected</div>
                    <div className='msc-gift__bonus__action'>
                        <button
                            className='modal-email-subscription__button'
                            // tslint:disable-next-line: jsx-no-lambda & react-this-binding-issue
                            onClick={() => _sendBonusProductToCart(parentState, parentId)}
                        >
                            {props.mfrmAddItemToCartText}
                        </button>
                    </div>
                </ModalFooter>
            </Modal>
        ));
    };

    const _selectedProductBonusItemsWithParentKey = (selectedArr: IProductBonusItems[] | undefined, parentId: number | undefined) => {
        let children: IMFIBonusItemDetails[] = [];
        selectedArr && selectedArr?.length > 0 && selectedArr?.map(obj => {
            if (obj.parentId === parentId) {
                children = obj.children;
            }
        });
        return children;
    };

    const _getSelectedBonusItems = (selectedItem: IMFIBonusItemDetails, parentId: number | undefined, parentState: IProductBonusItems[] | undefined) => {
        // let selectedItemsArray: IMFIBonusItemDetails[] | undefined = [...props?.sendStateValue] || [];
        let selectedItemsArray: IMFIBonusItemDetails[] | undefined = _selectedProductBonusItemsWithParentKey(parentState, parentId) || [];
        const newArr = [...selectedItemsArray];
        // const removeIndex = newArr.map((item) => item.BonusItemId).indexOf(selectedItem?.BonusItemId);
        let removeIndex = -1;
        hasArray(newArr)?.map((item, itemIndex) => {
            if(item.BonusItemId === selectedItem.BonusItemId && item.BonusProductId === selectedItem.BonusProductId) {
                removeIndex = itemIndex;
            }
        });
        if (removeIndex > -1) {
            newArr.splice(removeIndex, 1);
            selectedItemsArray = [...newArr];
        } else {
            if(hasArray(selectedItemsArray) && selectedItemsArray.length === totalBonusSelectedItems) {
                const intermediateArray = [...selectedItemsArray];
                intermediateArray.splice(0, 1);
                selectedItemsArray = [...intermediateArray];
            }
            selectedItemsArray.push(selectedItem);
        }
        const obj = {
            parentId: parentId,
            children: [...selectedItemsArray]
        };
        // const obj = {
        //     parentId: parentId,
        //     children: [selectedItem]
        // };
        props?.setCartLine && props?.setCartLine(obj);
    };

    const _sendBonusProductToCart = (parentState: IProductBonusItems[] | undefined, parentId: number | undefined) => {
        const finalBonusItemsSelected: IMFIBonusItemDetails[] | undefined = _selectedProductBonusItemsWithParentKey(parentState, parentId) || [];
        // const finalBonusItemsSelected = [...props?.sendStateValue];
        if (props.recievedBonusProductFromCartLine) {
            props?.recievedBonusProductFromCartLine(parentId, finalBonusItemsSelected, productParentQuantityCount);
            falseProductModal();
        }
    };
    /** VSI Customziation - End */

    const priceTextForScreenReader = () => {
        return (
            <span className='sr-only'>
                {`${resources.currentPriceText} ${discountPercentage >= 100 ? resources.freePriceText : discountedPrice}, ${
                    !!discountPercentage && discountPercentage > 0 ? `${resources.originalPriceText}, ${initialPrice}` : ''
                }`}
            </span>
        );
    };

    const _generatePrice = (className: string, isMobile?: boolean): React.ReactNode => {
        const errorMessage = _generateErrorMessage();
        if (props.isOutOfStock && isMobile) {
            if (errorMessage) {
                return [errorMessage];
            }

            return null;
        }
        let discountPercentage = 0;
        if (props.data.cartLine.DiscountAmount && props.data.cartLine.DiscountAmount > 0 && props.data.cartLine.NetPrice && props.data.cartLine.NetPrice > 0) {
            discountPercentage = Math.round((props.data.cartLine.DiscountAmount / props.data.cartLine.NetPrice) * 100);
        }
        const initialPrice = isSalesLine ? formatCurrency(props.data.cartLine.Price) : formatCurrency(props.data.cartLine.NetPrice);
        const discountedPrice = isSalesLine ? formatCurrency(props.data.cartLine.NetPrice) : formatCurrency(props.data.cartLine.NetAmountWithoutTax);
        const discountSaveText = props.resources.discountStringText;
        const splittedPrice = discountedPrice?.split('.');
        const splittedInitialPrice = initialPrice?.split('.');
        return (
            <div className={className}>
                {priceTextForScreenReader()}
                <span aria-hidden='true' className={!!discountPercentage && discountPercentage > 0 ? 'discounted' : ''}>
                    {discountPercentage >= 100 ? (
                        <span className='free'>{resources.freePriceText}</span>
                    ) : splittedPrice?.length ? (
                        <>
                            <span>{`${splittedPrice[0]}.`}</span>
                            <span className='msc-cart-line__product-cents'>{splittedPrice[1]}</span>
                        </>
                    ) : (
                        ''
                    )}
                </span>
                {!!discountPercentage && discountPercentage > 0 && (
                    <div aria-hidden='true' className='msc-disc-offer'>
                        {splittedInitialPrice?.length ? (
                            <span className='msc-price__strikethrough'>
                                {`${splittedInitialPrice[0]}.`}
                                <span className='msc-cart-line__product-cents'>{splittedInitialPrice[1]}</span>
                            </span>
                        ) : (
                            ''
                        )}
                        <span className='msc-discount-price'>{`${discountPercentage}${discountSaveText}`}</span>
                    </div>
                )}
            </div>
        );
    };

    const _generateQuantity = (): React.ReactNode => {
        const nodes = [];
        const errorMessage = _generateErrorMessage();
        if (props.isOutOfStock) {
            if (errorMessage) {
                return [errorMessage];
            }
            return null;
        }
        if (props.isQuantityEditable && !props.data.product?.IsGiftCard) {
            if (errorMessage) {
                nodes.push(errorMessage);
            }
            /* VSI Customization - Start - */
            // tslint:disable-next-line
            if (props.data.product) {
                nodes.push(
                    <IncrementalQuantity
                        id={`msc-cart-line__quantity_${props.data.product.RecordId}-${props.data.cartLine.DeliveryMode}-${props.data.cartLine.LineId}`}
                        max={props.maxQuantity || 20}
                        currentCount={props.currentQuantity}
                        inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                        onChange={onChange}
                        onDelete={onDelete}
                        decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                        incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                        key={props.data.cartLine.LineId}
                        disabled={!props.isCartStateReady}
                        isGiftCard={props.data.product.IsGiftCard}
                        telemetryContent={props.telemetryContent}
                        context={props.context}
                        bonusItem={bonusItemProduct}
                        productDeleteIcon={props.productDeleteIcon}
                        productIncrementIcon={props.productIncrementIcon}
                        productDecrementIcon={props.productDecrementIcon}
                    />
                );
            }
            /* VSI Customization - END -*/
        }
        return nodes;
    };

    const _generateAddGiftCta = (): JSX.Element[] | null => {
        const nodes = [];
        if(props.GiftCtaButton) {
            nodes.push(
                <div className='msc-add-gift'>
                    <button className='msc-add-gift__button' onClick={_addGiftItems}>{props.mfrmCtaGiftText}</button>
                </div>
            );
        }

        return nodes;
    };

    /* VSI Customization - End - 17-12-2020 */
    //product ratings' componant is generated
    const _generateRating = (): React.ReactNode => {
        const productAverageRatings = getBVAttributeValues(attr, 'BVAverageOverallRating');
        const productReviewCount = getBVAttributeValues(attr, 'BVTotalReviewCount');
        return (
                productReviewCount > 0 && (
                    <div className='msc-cart-line__product-rating-container' tabIndex={0}>
                        <div tabIndex={-1}>
                            <RatingComponent
                                avgRating={productAverageRatings}
                                readOnly={true}
                                ariaLabel={`${productAverageRatings} out of 5. Total reviews ${productReviewCount}`}
                                ratingCount={`${productReviewCount}`}
                                data={{}}
                                context={props.context}
                                id={cartModuleName}
                                typeName={cartModuleName}
                            />
                        </div>
                    </div>
                )
        );
    };

    const _generateProductImg = (): React.ReactNode => {
        const nodes = [];
        const productBadge = currentProductAttribute && _getProductBadges(props.context, currentProductAttribute);
        if (props.primaryImageUrl) {
            nodes.push(
                <>
                    <div className='msc-cart-line__image-badge'>
                        {productBadge}
                    </div>
                    <a className='msc-cart-line__product-image' href={productUrl} tabIndex={0}>
                        {/* VSI Customization - Start - 16-12-2020 */}
                        {/** Anchor tag added for click image redirect to pdp  */}
                        <div tabIndex={-1}>
                            <Image
                                role='img'
                                src={props.primaryImageUrl}
                                altText={`Product Image ${productWithBrandName}`}
                                gridSettings={props.gridSettings}
                                imageSettings={props.imageSettings}
                                fallBackSrc={fallbackImageUrl}
                                bypassHideOnFailure={true}
                            />
                        </div>
                        {/* VSI Customization - End - 16-12-2020 */}
                    </a>
                </>
            );
        }
        return nodes;
    };

    if (props.displayMode === 'COMPACT') {
        const reducedDimensions: string = product?.Dimensions ? product.Dimensions.reduce<string>(
            (str, productDimension) => {
                if (productDimension.DimensionValue && productDimension.DimensionValue.Value) {
                    if (str) {
                        return `${str}, ${productDimension.DimensionValue.Value}`;
                    }

                    return `${productDimension.DimensionValue.Value}`;
                }

                return str;
            },
            '') : '';
        return (
            <div className='msc-cart-line msc-cart-line__compact'>
                <a className='msc-cart-line__product-image' href={productUrl} title={productWithBrandName} tabIndex={0}>
                    {/* VSI Customization - Start - 16-12-2020 */}
                    {/** Anchor tag added for click image redirect to pdp  */}
                    <div tabIndex={-1}>
                        <Image role='img' src={props.primaryImageUrl} altText={`Product Image ${productWithBrandName}`} gridSettings={props.gridSettings} imageSettings={imageSettings} fallBackSrc={fallbackImageUrl} bypassHideOnFailure={true} />
                    </div>
                    {/* VSI Customization - End - 16-12-2020 */}
                </a>
                <div className='msc-cart-line__content'>
                    <a className='msc-cart-line__product-title' href={productUrl} {...prodAttribute} >{productWithBrandName}</a>
                    {reducedDimensions !== '' ?
                        <div className='msc-cart-line__product-variants'>{reducedDimensions}</div> :
                        ''
                    }
                    {_generateQuantity()}
                    {_generatePrice('msc-cart-line__product-price')}
                </div>
            </div>
        );
    }
    const discountedPrice1 = isSalesLine ? formatCurrency(props.data.cartLine.NetPrice) : formatCurrency(props.data.cartLine.NetAmountWithoutTax);
    let deliveryMsgCore = '', deliveryNonCoreMsg: string | undefined = '', coreDeliveryMessage = '', coreDeliverySlot = '', nonCoreDeliveryMsg = '';

    const { deliveryByMessagesCore } = props;
    const productRecordId = product?.RecordId?.toString();
    if (deliveryByMessagesCore?.length > 0) {
        const coreDeliveryMsg = deliveryByMessagesCore?.find(prod => prod.variantID === productRecordId);
        if (coreDeliveryMsg) {
        const match = RegExp(cartDeliveryMsgRegex)?.exec(coreDeliveryMsg?.deliveryByMessage) || [];
          if (match[0]) {
            coreDeliverySlot = match[0];
            coreDeliveryMessage = coreDeliveryMsg?.deliveryByMessage?.split(match[0])?.[0];
          } else {
            coreDeliverySlot = props.resources.sixPlusWeeks;
            coreDeliveryMessage = props.resources.deliveryIn;
          }
          deliveryMsgCore = coreDeliveryMsg?.deliveryByMessage;
        }
    }
    if(props.deliveryByMessageSPDS !== undefined && props.deliveryByMessageSPDS.length > 0) {
        for(const msgNonCore of props.deliveryByMessageSPDS) {
            if(msgNonCore && (msgNonCore?.variantID === product?.RecordId?.toString())) {
                deliveryNonCoreMsg = msgNonCore.deliveryBySlot;
                nonCoreDeliveryMsg = msgNonCore.deliveryCartMsg || '';
            }
        }
    }
    const promoCodesText = `${promoRef.current?.innerText ? `${promoAriaText}, ${promoRef.current?.innerText?.split('\n').join(', ')}.`: ''}`;
    const deliveryMessageText = !props.hideDeliveryMessage && deliveryNonCoreMsg && deliveryNonCoreMsg.length > 0 ? deliveryNonCoreMsg : deliveryMsgCore;
    const initialPrice = isSalesLine ? formatCurrency(props.data.cartLine.Price) : formatCurrency(props.data.cartLine.NetPrice);
    const discountedPrice = isSalesLine ? formatCurrency(props.data.cartLine.NetPrice) : formatCurrency(props.data.cartLine.NetAmountWithoutTax);
    let discountPercentage = 0;
        if (props.data.cartLine.DiscountAmount && props.data.cartLine.DiscountAmount > 0 && props.data.cartLine.NetPrice && props.data.cartLine.NetPrice > 0) {
            discountPercentage = Math.round((props.data.cartLine.DiscountAmount / props.data.cartLine.NetPrice) * 100);
        }
    const productPriceAriaLabel = discountPercentage > 0 ? `${(discountPercentage >= 100) ? 'FREE' : discountedPrice} ${format(discountPercentageAriaText, `${discountPercentage}${discountStringText}` || '')} ${initialPrice}` : initialPrice;
    const productAriaLabel = `${productAriaText}, ${productWithBrandName}. ${promoCodesText} ${variantAriaText}, ${productDimensionVariants} ${deliveryMessageText?.replace(/\s+$/, '')}. ${priceAriaText}, ${productPriceAriaLabel}. ${productPageLinkAriaLabel}`;
    const currentProductAttribute = props.data.productAttributes?.find(p => p?.productId === cartLine?.ProductId)?.attributeValue;
    const promotionalMessage = currentProductAttribute && getProductAlertBanner(props.context, currentProductAttribute);
    // VSI Customization - Product badges
    const isDeliveryMsgLoaded = (deliveryNonCoreMsg || coreDeliverySlot);
    return (
        <>
            <div className='msc-cart-line'>
                {/* rating generated for mobile screen */}
                <div className='msc-cart-line__product-additional-info-mobile'>
                    {_generateRating()}
                    <div className='msc-cart-line__product-eye-brow-section' tabIndex={0}><div className='msc-cart-line__product-eye-brow-container' tabIndex={-1}>{getEyeBrowMsg(productAttributes, product?.RecordId, props.eyeBrowIcons)}</div></div>
                </div>
                <div className='msc-cart-line__container'>
                    {_generateProductImg()}
                    <div className='msc-cart-line__product-description'>
                        <div className='msc-cart-line__product-additional-info-desktop'>
                            {_generateRating()}
                            <div className='msc-cart-line__product-eye-brow-section' tabIndex={0}><div className='msc-cart-line__product-eye-brow-container' tabIndex={-1}>{getEyeBrowMsg(productAttributes, product?.RecordId, props.eyeBrowIcons)}</div></div>
                        </div>
                        <div className='msc-cart-line__content'>
                            <div
                                className='msc-cart-line__product'
                                // data-productBrand={product?.}
                                // data-productCategory={product?.Price}
                                data-productId={product?.ItemId}
                                data-productName={productWithBrandName}
                                data-productPrice={discountedPrice1?.slice(1)}
                                data-productQuantity={props.currentQuantity}
                            // data-productVariant={productDimensions}
                            >
                                {/* productDimension.DimensionValue && productDimension.DimensionValue.Value */}
                                <a className='msc-cart-line__product-title' role='link' aria-label={productAriaLabel} href={productUrl}>{productWithBrandName?.split('\n')?.slice(0, 3)?.join('\n')}</a>
                                {productDimensions && productDimensions.length > 0 ?
                                    <>
                                        <div className='msc-cart-line__product-variants'>{productDimensions}</div>
                                        {!props.hideDeliveryMessage && <p className='cart-delivery-message'>
                                            {isDeliveryMsgLoaded ?
                                                (deliveryNonCoreMsg && deliveryNonCoreMsg.length > 0 ? <>{`${nonCoreDeliveryMsg} `} <span className='cart-delivery-date'>{deliveryNonCoreMsg} </span></> : <>{`${coreDeliveryMessage} `} <span className='cart-delivery-date'>{coreDeliverySlot}</span></>)
                                                : !props.emptydeliveryMsgForCore && <CustomSkeleton type='text' width='30%' height='16px' />
                                            }
                                        </p>
                                        }
                                    </> :
                                    ''
                                }
                                {renderInventoryLabel}
                                {props.showShippingChargesForLineItems &&
                                    <div className='msc-cart-line__freight'>
                                        {renderShippingLabel}
                                    </div>
                                }
                                {renderOtherCharges}
                                {parentProduct && props.data.cartLine?.DiscountAmount === 0 ? '' : <div className='msc-cart-line-item-product-discount-container' ref={promoRef}>{renderDisountLines}</div>}
                                <div className='msc-edit-gift'>
                                    {editGiftCta && <button role='button' className='msc-edit-gift__button' aria-label={`${editGiftCtaText}, ${dialogAriaText}`} onClick={_addGiftItems}>{editGiftCtaText}</button>}
                                </div>
                            </div>
                            {/** VSI Customization - Start - 01-12-2023 */}
                            {/* Quantity Selector and price components are rendered here for the desktop devices and made hidden using CSS
                                on the mobile view. Also the quantity selector was getting duplicated on mobile screen during loading, which is fixed here. */}
                            <div className='msc-cart-line__quantity-section-desktop'>
                                {_generateQuantity()}
                                {_generatePrice('msc-cart-line__product-price')}
                            </div>
                            {/** VSI Customization - End - 01-12-2023 */}
                        </div>
                    </div>
                </div>
                {/** VSI Customization - Start - 01-12-2023 */}
                {/* Quantity Selector and price components are rendered here for the mobile devices and made hidden using CSS
                    on the desktop view. */}
                <div className='msc-cart-line__mobile-container'>
                    {_generateQuantity()}
                    {_generatePrice('msc-cart-line__product-price', true)}
                </div>
                {/** VSI Customization - End - 01-12-2023 */}
            </div>
            {promotionalMessage && <div className='msc-cart__promotion-msg-section' tabIndex={0}><div tabIndex={-1}><RichTextComponent className='msc-cart__promotion-msg-text' text={promotionalMessage}/></div></div>}
            {_generateAddGiftCta()}
            {_createModal()}
        </>
    );
};

// @ts-ignore
export const CartLineItemComponent: React.FunctionComponent<ICartLineProps> = msdyn365Commerce.createComponentOverride<ICartline>(
    'CartLineItem',
    { component: CartLine, ...CartLineItemFunctions }
);

export default CartLineItemComponent;